import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Package, Truck, CheckCircle, Clock, MapPin, ArrowLeft,
  CircleDot, ShoppingBag, Loader2,
} from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const statusSteps = [
  { key: "pending", label: "Order Placed", icon: ShoppingBag },
  { key: "confirmed", label: "Confirmed", icon: CheckCircle },
  { key: "shipped", label: "Shipped", icon: Package },
  { key: "out_for_delivery", label: "Out for Delivery", icon: Truck },
  { key: "delivered", label: "Delivered", icon: MapPin },
];

const TrackOrderPage = () => {
  const { orderId } = useParams();
  const { API, settings } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchId, setSearchId] = useState("");

  useEffect(() => {
    if (orderId) fetchOrder(orderId);
    else setLoading(false);
  }, [orderId]);

  const fetchOrder = async (id) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orders/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error("Failed to fetch order:", error);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchId.trim()) {
      fetchOrder(searchId.trim());
    }
  };

  const getCurrentStepIndex = () => {
    if (!order) return -1;
    if (order.order_status === "cancelled") return -2;
    return statusSteps.findIndex((s) => s.key === order.order_status);
  };

  const currentStep = getCurrentStepIndex();

  const shareOnWhatsApp = () => {
    if (!order) return;
    const itemsList = order.items.map((i) => `${i.product_name} x${i.quantity}`).join(", ");
    const msg = `Hi! My order #${order.id.slice(0, 8)} status: ${order.order_status.replace("_", " ")}. Items: ${itemsList}. Total: ₹${order.total}`;
    window.open(`https://wa.me/${settings?.whatsapp_number || "919441114344"}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="min-h-screen py-8" data-testid="track-order-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
        {/* Back link */}
        <Link to="/my-account" className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to My Account</span>
        </Link>

        <h1 className="text-3xl font-bold mb-6">Track Your Order</h1>

        {/* Search by Order ID */}
        {!orderId && (
          <div className="bg-white rounded-xl border p-6 mb-8">
            <p className="text-muted-foreground mb-4">Enter your Order ID to track your delivery</p>
            <form onSubmit={handleSearch} className="flex gap-3">
              <Input
                value={searchId}
                onChange={(e) => setSearchId(e.target.value)}
                placeholder="Enter Order ID"
                className="flex-1"
                data-testid="track-order-input"
              />
              <Button type="submit" data-testid="track-order-button">Track</Button>
            </form>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : order ? (
          <div className="space-y-6">
            {/* Order Header */}
            <div className="bg-white rounded-xl border p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Order ID</p>
                  <p className="font-mono font-semibold text-lg" data-testid="order-id">{order.id}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Placed on {new Date(order.created_at).toLocaleDateString("en-IN", {
                      year: "numeric", month: "long", day: "numeric",
                    })}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={shareOnWhatsApp} data-testid="whatsapp-support">
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp Support
                  </Button>
                </div>
              </div>
            </div>

            {/* Tracking Progress */}
            <div className="bg-white rounded-xl border p-6" data-testid="tracking-progress">
              <h2 className="text-lg font-bold mb-6">Delivery Status</h2>

              {order.order_status === "cancelled" ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Package className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-red-600">Order Cancelled</h3>
                  <p className="text-muted-foreground mt-2">This order has been cancelled</p>
                </div>
              ) : (
                <div className="relative">
                  {statusSteps.map((step, index) => {
                    const isCompleted = index <= currentStep;
                    const isCurrent = index === currentStep;
                    const StepIcon = step.icon;

                    return (
                      <div key={step.key} className="flex gap-4 mb-8 last:mb-0">
                        {/* Line + Circle */}
                        <div className="flex flex-col items-center">
                          <div
                            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              isCompleted
                                ? "bg-primary text-white"
                                : "bg-muted text-muted-foreground"
                            } ${isCurrent ? "ring-4 ring-primary/20" : ""}`}
                          >
                            <StepIcon className="w-5 h-5" />
                          </div>
                          {index < statusSteps.length - 1 && (
                            <div
                              className={`w-0.5 flex-1 min-h-[40px] mt-2 ${
                                index < currentStep ? "bg-primary" : "bg-muted"
                              }`}
                            />
                          )}
                        </div>

                        {/* Content */}
                        <div className="pt-1.5 pb-2">
                          <p className={`font-semibold ${isCompleted ? "text-foreground" : "text-muted-foreground"}`}>
                            {step.label}
                          </p>
                          {/* Show timestamp from tracking array if available */}
                          {order.tracking?.find((t) => t.status === step.key) && (
                            <p className="text-sm text-muted-foreground mt-1">
                              {new Date(order.tracking.find((t) => t.status === step.key).timestamp).toLocaleDateString("en-IN", {
                                year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                              })}
                            </p>
                          )}
                          {isCurrent && (
                            <span className="inline-block mt-2 px-3 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                              Current Status
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-bold mb-4">Order Items</h2>
              <div className="space-y-3">
                {order.items.map((item, index) => (
                  <div key={index} className="flex gap-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                      <img
                        src={item.image_url || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100"}
                        alt={item.product_name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.product_name}</p>
                      <p className="text-sm text-muted-foreground">Qty: {item.quantity} × ₹{item.price.toLocaleString()}</p>
                    </div>
                    <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </div>

              <hr className="my-4" />
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span>₹{order.subtotal.toLocaleString()}</span>
                </div>
                {order.coupon_discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Coupon ({order.coupon_code})</span>
                    <span>-₹{order.coupon_discount.toLocaleString()}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Delivery</span>
                  <span>{order.delivery_charge === 0 ? "FREE" : `₹${order.delivery_charge}`}</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t">
                  <span>Total</span>
                  <span className="text-primary">₹{order.total.toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Delivery Address */}
            <div className="bg-white rounded-xl border p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5" />
                Delivery Address
              </h2>
              <div className="text-muted-foreground">
                <p className="font-medium text-foreground">{order.delivery_address.full_name}</p>
                <p>{order.delivery_address.address_line1}</p>
                {order.delivery_address.address_line2 && <p>{order.delivery_address.address_line2}</p>}
                <p>{order.delivery_address.city}, {order.delivery_address.state} - {order.delivery_address.pincode}</p>
                <p>Phone: {order.delivery_address.phone}</p>
              </div>
            </div>
          </div>
        ) : orderId ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Order Not Found</h3>
            <p className="text-muted-foreground mb-6">We couldn't find an order with this ID</p>
            <Link to="/categories">
              <Button>Continue Shopping</Button>
            </Link>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TrackOrderPage;
